/**
 * UnmappedFieldsUI
 *
 * Renders an editor for every persisted field that the active UI layout does
 * not place, so nothing silently disappears from a form or entity record.
 * Consumer: `RenderUIElement`, once per rendered layout root.
 *
 * Deliberately coarse over `fields` — enumerating them IS its job, so a
 * keystroke in any unmapped field re-renders it. What it must NOT do is widen
 * that subscription: it takes `fields` through `useCurrentArtifact` and reads
 * access and the version writer directly, never through the page-level
 * `useFormStore` (SYD-2252), which would also tie it to attachments, workflow
 * state, layouts and the three collections.
 *
 * Every keystroke stamps document-level `meta.lastModified` alongside the
 * per-field one: the client persist layer bumps only `modifiedAt`, and a form
 * version saved without that stamp is a retained version point-in-time
 * recovery can neither name nor reach (SYD-2450).
 *
 * That stamp is also why `disabled` exists: these controls write to the live
 * document, so a caller holding the rest of the form read-only — a recovery
 * preview — must be able to hold these too, or a keystroke here mints a restore
 * point mid-preview and moves the baseline the operator's own restore is checked
 * against. The controls are disabled rather than hidden.
 *
 * Each field stamps `data-form-field-key` and registers its writer with
 * `formFieldClipboard`, so the form's right-click menu can paste into it. The
 * paste lands through the same `handleFieldChange` a keystroke uses and inherits
 * this surface's behaviour — no validation, no undo entry — rather than `FieldUI`'s
 * (SYD-2232). A read-only surface registers NO writer, which is what greys Paste
 * Field out rather than letting a click spend a clipboard read on nothing; the
 * refusal inside `handleFieldChange` remains the real gate, because the clipboard
 * read the menu item sits behind spans an `await`.
 */

import { FC, useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { Collapse, CollapseProps, Typography, Tag, Alert } from 'antd';
import {
  Fields,
  UIElement,
  UIElementType,
  AgentType,
  FieldType,
} from '#types/System.types';
import ControlUI from './ControlUI';
import { UserContext } from '../../providers/UserContext';
import { FieldTypeContext } from '../../providers/FieldTypeContext';
import { Timestamp } from 'firebase/firestore';
import { useCurrentArtifact } from '../../use/useCurrentArtifact';
import { useEvaluateFormAccess } from '../../use/data/useEvaluateFormAccess';
import { formStore, useEntityRecordScope } from '../../store';
import { formFieldClipboard } from '../../store/form';
import FlexBox from '../atoms/FlexBox';
import defineFieldTypeFromDataType from './defineFieldTypeFromDataType';

/**
 * Props for the UnmappedFieldsUI component
 */
interface UnmappedFieldsUIProps {
  /** The UI element structure to check for unmapped fields */
  uiElement: UIElement;
  /**
   * Forces every unmapped field read-only whatever access says, the way `FormUI`
   * forces its mapped ones. A point-in-time recovery preview sets it: an edit made
   * while it is open writes to the live document, mints a restore point mid-preview,
   * and moves the very `liveLastModifiedMillis` the operator's own restore is checked
   * against (SYD-2450).
   */
  disabled?: boolean;
}

/**
 * Recursively extracts all field IDs that are displayed in the UI structure
 * @param {UIElement} element - The UI element to traverse
 * @returns {string[]} Array of field IDs that are displayed
 */
const getDisplayedFields = (element: UIElement): string[] => {
  if (!element) {
    return [];
  }
  if (element.uiElementType === UIElementType.Field) {
    return [element.fieldId];
  } else if (element.uiElementType === UIElementType.EntityReference) {
    return [];
  } else if ('children' in element) {
    return element.children.flatMap(getDisplayedFields);
  }
  return [];
};

/**
 * Displays fields that exist in the data but are not mapped to any UI elements.
 * Shows these fields in a collapsible panel allowing users to view and edit them.
 * 
 * @component
 * @param {UnmappedFieldsUIProps} props - Component props
 * @param {UIElement} props.uiElement - The UI element structure to analyze for field mappings
 * @param {boolean} [props.disabled] - Forces every field read-only, whatever access says
 *
 * @returns {JSX.Element | null} A collapsible panel with unmapped fields or null if none exist
 *
 * @example
 * <UnmappedFieldsUI uiElement={formStructure} disabled={previewing} />
 */
const UnmappedFieldsUI: FC<UnmappedFieldsUIProps> = ({ uiElement, disabled = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user, hasEntityAccess } = useContext(UserContext);
  const { fieldTypes } = useContext(FieldTypeContext);
  const artifact = useCurrentArtifact();
  const insideFormContext = artifact.insideFormContext;
  const insideEntityContext = artifact.insideEntityContext;
  const data = artifact.kind === 'none' ? null : artifact.data;
  const formId = artifact.kind === 'form' ? artifact.formId : undefined;
  const versionId = artifact.kind === 'form' ? artifact.versionId : undefined;
  const scope = useEntityRecordScope();
  const formAccess = useEvaluateFormAccess({ formId, versionId, enabled: insideFormContext });
  const formReadOnly = !formAccess.canWrite;
  const entityLocked = scope.entityLocked;
  const debouncedSaveEntityRecord = scope.debouncedSetEntityRecord;

  const { extraFields, effectiveFieldTypes } = useMemo(() => {
    const displayedFields = uiElement ? getDisplayedFields(uiElement) : [];
    const fields: Fields = data?.fields ?? {};
    const extraFields = Object.entries(fields).filter(
      ([fieldName]) => !displayedFields.includes(fieldName)
    );
    
    const effectiveFieldTypes = extraFields.reduce((acc, [fieldName, field]) => {
      const fieldType = fieldTypes[fieldName];
      const fieldDataTypeId = fieldType?.fieldDataTypeId;

      if (!fieldDataTypeId) {
        const inferredType = defineFieldTypeFromDataType(field?.value);
        acc[fieldName] = {
          fieldDataTypeId: inferredType as any,
        } as FieldType;
      } else {
        acc[fieldName] = fieldType;
      }

      return acc;
    }, {} as Record<string, FieldType | undefined>);

    return { extraFields, effectiveFieldTypes };
  }, [uiElement, data?.fields, fieldTypes]);

  /**
   * `disabled` outranks access on both sides. It is what a caller uses to hold the
   * whole document read-only for a reason access knows nothing about — a recovery
   * preview above all, where a keystroke here would write to the live document while
   * the banner says the form is read-only.
   */
  const isReadOnly = disabled || (data && 'fields' in data
    ? !hasEntityAccess || entityLocked
    : formReadOnly);

  /**
   * The refusal lives here, on the single write path, the way `useFieldUI`'s
   * `commitValue` refuses: a paste reads the clipboard across an `await`, so the
   * `writeRefused` the menu item was rendered with can be stale by the time the
   * value arrives — a recovery preview opening mid-read is exactly that.
   */
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    if (isReadOnly) return;
    const now = Timestamp.now();
    const updateObject = {
      fields: {
        [fieldName]: {
          value: value,
          meta: {
            lastModified: now,
            userId: user?.uid,
            agentType: AgentType.User,
          }
        }
      },
      meta: {
        lastModified: now,
        userId: user?.uid,
        agentType: AgentType.User,
      }
    };

    if (insideEntityContext) {
      debouncedSaveEntityRecord(updateObject);
    } else if (insideFormContext) {
      void formStore.getFormVersionPersist()?.setMerge(updateObject, { debounce: true });
    }
  }, [isReadOnly, insideEntityContext, debouncedSaveEntityRecord, insideFormContext, user?.uid]);

  /**
   * The document these fields belong to, on the entity-before-form precedence
   * `useFieldUI` resolves a field's path with, so an unmapped `notes` and a mapped
   * one on the same document are one key apart and an embedded record's `notes` is
   * a different key entirely. `null` outside both documents.
   */
  const path = insideEntityContext && scope.entityId && scope.recordId
    ? `entities/${scope.entityId}/records/${scope.recordId}`
    : insideFormContext && formId && versionId
      ? `forms/${formId}/formVersions/${versionId}`
      : null;

  /**
   * What a registration resolves at click time, as last rendered: the values, the
   * inferred field types and the writer. Every one of the three is a fresh object
   * on each render, so reaching them through refs is what lets the registration
   * itself stand still while the form is typed in.
   */
  const fieldsRef = useRef(data?.fields);
  fieldsRef.current = data?.fields;
  const effectiveFieldTypesRef = useRef(effectiveFieldTypes);
  effectiveFieldTypesRef.current = effectiveFieldTypes;
  const handleFieldChangeRef = useRef(handleFieldChange);
  handleFieldChangeRef.current = handleFieldChange;

  /**
   * The unmapped field names as one primitive, and back as an array that keeps one
   * identity for as long as the SET is unchanged. `extraFields` is rebuilt on every
   * keystroke — `data.fields` is a fresh object after each write — so registering
   * through it would unregister and re-register every unmapped field per character.
   * NUL separates, because no Firestore field name contains one.
   */
  const registeredFieldNamesKey = extraFields.map(([fieldName]) => fieldName).join('\u0000');
  const registeredFieldNames = useMemo(
    () => registeredFieldNamesKey.split('\u0000').filter((fieldName) => fieldName !== ''),
    [registeredFieldNamesKey],
  );

  useEffect(() => {
    if (path === null) return;
    const unregisters = registeredFieldNames.map((fieldName) =>
      formFieldClipboard.registerTarget(`${path}#${fieldName}`, {
        read: () => fieldsRef.current?.[fieldName]?.value,
        fieldId: fieldName,
        fieldType: effectiveFieldTypesRef.current[fieldName],
        write: isReadOnly ? null : (value) => handleFieldChangeRef.current(fieldName, value),
      }),
    );
    return () => unregisters.forEach((unregister) => unregister());
  }, [path, registeredFieldNames, isReadOnly]);

  if (!insideEntityContext && !insideFormContext) {
    return <Alert title="Error: Neither entity nor form context is available." type="error" />;
  }

  if (extraFields.length === 0) {
    return null;
  }

  const items: CollapseProps['items'] = [
    {
      key: '1',
      forceRender: false,
      label: (
        <FlexBox>
          <span>
            Additional Fields
          </span>
          <Tag style={{ margin: 0 }}>
            {extraFields.length} fields
          </Tag>
        </FlexBox>
      ),
      children: extraFields.map(([fieldName, field]) => (
        <FlexBox
          key={fieldName}
          column
          justifyStart
          alignStart
          gap={0}
          stretch
          style={{
            marginBottom: 10,
          }}
          data-form-field-key={path === null ? undefined : `${path}#${fieldName}`}
        >
          <Typography.Text>{fieldName}</Typography.Text>
          <ControlUI
            enableUndefinedFieldTypes={true}
            value={field?.value}
            uiField={{
              uiElementType: UIElementType.Field,
              fieldId: fieldName,
            }}
            readOnly={isReadOnly}
            onChange={(value: unknown) => handleFieldChange(fieldName, value)}
            fieldType={effectiveFieldTypes[fieldName]}
          />
        </FlexBox>
      ))
    }
  ];

  return (
    <Collapse
      style={{
        marginTop: 10,
        width: '100%',
      }}
      bordered={false}
      items={items}
      activeKey={isExpanded ? ['1'] : []}
      onChange={() => setIsExpanded(!isExpanded)}
      size='small'
      destroyOnHidden
    />
  );
};

export default UnmappedFieldsUI;